import * as React from 'react';

import { Button } from '@shared/components/bootstrap';

import { Forgive as BillingInvoiceForgive } from '@admin/components/billing/invoices/forgive';
import { MarkAsPaid as BillingInvoiceMarkAsPaid } from '@admin/components/billing/invoices/mark_as_paid';
import { ReBill as BillingInvoiceReBill } from '@admin/components/billing/invoices/re_bill';
import { IInvoiceActionProps } from '@admin/components/billing/invoices/types';
import { Action as BillingRefundAction } from '@admin/components/billing/refunds/action';
import { Permission } from '@admin/components/helpers/permission';
import { BILLING } from '@admin/libraries/access_control_catalog.generated';

import { InvoicePayModal } from './invoice_pay_modal';
import type { IFullInvoice, IInvoiceRow } from './types';

const lastCharge = (invoice: IInvoiceRow) => invoice.charges.find((c) => c.stripe_id === invoice.last_stripe_charge_id);

export const InvoiceRowActions: React.FC<{
  invoice: IFullInvoice | IInvoiceRow;
  onExpand(): void;
  onInvoiceActionSave: IInvoiceActionProps['onSave'];
}> = ({ invoice, onExpand, onInvoiceActionSave }) => {
  const [payOpen, setPayOpen] = React.useState(false);
  const refundable = lastCharge(invoice);

  const payButton = (
    <Button kind="primary" onClick={() => setPayOpen(true)} disabled={!invoice.payable}>
      Make Payment
    </Button>
  );

  return (
    <div>
      <div className="clearfix" />
      <Permission resource={BILLING.resource} action={BILLING.actions.update} denied={null}>
        {invoice.payable && (
          <BillingInvoiceForgive
            invoiceID={invoice.id}
            accountID={invoice.account_id}
            amountOwed={invoice.amount_owed}
            onSave={onInvoiceActionSave}
          />
        )}
        {invoice.payable && payButton}
        {invoice.payable && (
          <BillingInvoiceMarkAsPaid
            invoiceID={invoice.id}
            accountID={invoice.account_id}
            amountOwed={invoice.amount_owed}
            onSave={onInvoiceActionSave}
          />
        )}
        {invoice.admin_refundable && !invoice.multiple_invoice && refundable && (
          <BillingRefundAction refundable={{ id: String(refundable.id), amount: refundable.amount }} />
        )}
        {invoice.admin_refundable && invoice.multiple_invoice && (
          <Button kind="default" onClick={onExpand} disabled={!!invoice.open}>
            Expand to Refund
          </Button>
        )}
        {!!invoice.rebill_status && (
          <BillingInvoiceReBill
            invoiceID={invoice.id}
            accountID={invoice.account_id}
            amountOwed={invoice.total}
            onSave={onInvoiceActionSave}
            disabled={Number(invoice.tax) > 0 && Number(invoice.discount_amount) > 0}
          />
        )}
      </Permission>

      {payOpen && invoice.payable && (
        <InvoicePayModal
          invoice={invoice}
          onClose={() => setPayOpen(false)}
          onComplete={() => {
            setPayOpen(false);
            onInvoiceActionSave('', 'Invoice Successfully paid!', false);
          }}
        />
      )}
    </div>
  );
};
